import { KeysToCamelCase, Visualisation } from '@statsbomb/parachute-types';
import { Atom } from 'jotai';
import { PITCH_COLOURING_MODES, PITCH_CROP, PITCH_ORIENTATION } from '@/consts/visualisations';
import { PromiseAtom } from './atom';
import { ArrowEvent, MarkerEvent } from './event';
import { SBID } from './generic';

export type PitchOrientation = (typeof PITCH_ORIENTATION)[number];
export type PitchCrop = (typeof PITCH_CROP)[number];

export type ConfigVisualisation = KeysToCamelCase<Visualisation>;
export interface VisualisationComponentProps {
  allowInteraction?: boolean;
  rotationName?: string;
  visibleArea?: string;
}

export interface PitchVisComponentProps extends VisualisationComponentProps {
  isPreview?: boolean;
}

export interface PitchVisComponents {
  heatMap: (props: PitchVisComponentProps) => JSX.Element;
  eventMap: (props: PitchVisComponentProps) => JSX.Element;
  passSonar: (props: PitchVisComponentProps) => JSX.Element;
  shotMap: (props: PitchVisComponentProps) => JSX.Element;
  eventBuckets: (props: PitchVisComponentProps) => JSX.Element;
}

export const SUPPORTED_PITCH_VISUALISATION_KEYS: readonly string[] = [
  'eventMap',
  'heatMap',
  'passSonar',
  'shotMap',
  'eventBuckets',
] as const satisfies readonly (keyof PitchVisComponents)[];

interface WidgetVisComponent extends VisualisationComponentProps {
  isWidget: boolean;
  id: SBID;
}

export interface VisComponents extends PitchVisComponents {
  radar: (props: WidgetVisComponent) => JSX.Element;
  positionMap?: (props: WidgetVisComponent) => JSX.Element;
  info?: (props: WidgetVisComponent) => JSX.Element;
  squad?: (props: WidgetVisComponent) => JSX.Element;
  minutesPlayed?: (props: WidgetVisComponent) => JSX.Element;
}

export enum VisDisplayMode {
  BOTH = 'both',
  VIS = 'vis',
  TABLE = 'table',
}

export type VisualisationMap = Record<keyof PitchVisComponents, (props: VisualisationComponentProps) => JSX.Element>;

export interface FallBackProps {
  isPreview?: boolean;
}

export interface PitchEvents {
  markerEvents: MarkerEvent[];
  arrowEvents: ArrowEvent[];
}

export type PitchEventsPromiseAtom = PromiseAtom<PitchEvents>;
export type UnwrappedPitchEventsAtom = Atom<PitchEvents>;

export interface EventMapBaseProps {
  allowInteraction?: boolean;
  unwrappedPitchEventsAtom: UnwrappedPitchEventsAtom;
  isPreview?: boolean;
  swapCropUnderAgainst?: boolean;
}

export interface EventMapProps extends EventMapBaseProps {
  pitchEventsAtom: PitchEventsPromiseAtom;
}

export interface HeatMapProps {
  pitchEventsAtom: PitchEventsPromiseAtom;
  unwrappedPitchEventsAtom: UnwrappedPitchEventsAtom;
  allowInteraction?: boolean;
  swapCropUnderAgainst?: boolean;
}

export type EventBucketsProps = Omit<HeatMapProps, 'swapCropUnderAgainst'>;

export type ShotMapEventMarker = Pick<
  MarkerEvent,
  | 'type'
  | 'eventId'
  | 'outcome'
  | 'xg'
  | 'obv'
  | 'penalty'
  | 'freeKick'
  | 'bodyPart'
  | 'startX'
  | 'startY'
  | 'followsDribble'
  | 'followsThroughBall'
>;

export type PitchMarkerColouringMode = (typeof PITCH_COLOURING_MODES)[number];
